import earcut from 'earcut';
import { getStoreyElevation } from '@shared/helpers/design/storey.helpers';
import { getShapeRingCoordinates } from '../helpers/map.helpers';
import type { Coordinate } from '@shared/models/geography.model';
import type { Storey } from '@shared/models/design/storey.model';

/**
 * Projects a coordinate at an altitude into the renderer's local space.
 * @returns Position as `[x, y, z]`
 */
export type ToLocalPosition = (
  lng: number,
  lat: number,
  altitudeMeters: number,
) => [number, number, number];

/**
 * Solid triangles covering a building's volume, used as the mask the
 * silhouette outline is detected from (DEV-2576).
 *
 * Only the shape matters, so there are no normals, colours or cutout holes:
 * an atrium is left filled on purpose, since the outline traces the building's
 * outer contour rather than every opening. Mezzanines sit inside the volume
 * and are skipped for the same reason.
 *
 * @param storeys Storeys of the building
 * @param origin Design origin the footprints are measured from
 * @param project Projection into the renderer's local space
 * @param includeMezzanines Include an isolated hovered mezzanine in the contour mask
 * @returns Triangle positions, three floats per vertex
 */
export const buildBuildingMassVertices = ({
  storeys,
  origin,
  project,
  includeMezzanines = false,
}: {
  storeys: Storey[] | undefined;
  origin: Coordinate;
  project: ToLocalPosition;
  includeMezzanines?: boolean;
}): Float32Array => {
  if (!storeys?.length) return new Float32Array(0);
  const positions: number[] = [];

  for (const storey of storeys) {
    if (storey.mezzanine && !includeMezzanines) continue;
    const base = getStoreyElevation(storeys, storey);
    const top = base + storey.height;

    for (const shape of storey.shapes ?? []) {
      if (shape.cutout === true) continue;
      const ring = getShapeRingCoordinates(shape, origin);
      // getShapeRingCoordinates closes the ring; earcut wants it open.
      const open = ring.length > 1 ? ring.slice(0, -1) : [];
      if (open.length < 3) continue;

      const atBase = open.map(([lng, lat]) => project(lng, lat, base));
      const atTop = open.map(([lng, lat]) => project(lng, lat, top));

      // Roof and floor close the volume so the mask has no holes to leak
      // through when the camera looks along a wall.
      const flat = atTop.flatMap(([x, y]) => [x, y]);
      const indices = earcut(flat);
      for (let index = 0; index < indices.length; index += 3) {
        const a = indices[index];
        const b = indices[index + 1];
        const c = indices[index + 2];
        if (a === undefined || b === undefined || c === undefined) continue;
        for (const corner of [a, b, c]) {
          const top3 = atTop[corner];
          if (top3) positions.push(top3[0], top3[1], top3[2]);
        }
        for (const corner of [a, b, c]) {
          const base3 = atBase[corner];
          if (base3) positions.push(base3[0], base3[1], base3[2]);
        }
      }

      open.forEach((_, index) => {
        const next = (index + 1) % open.length;
        const a = atBase[index];
        const b = atBase[next];
        const bTop = atTop[next];
        const aTop = atTop[index];
        if (!a || !b || !bTop || !aTop) return;
        positions.push(...a, ...b, ...bTop, ...a, ...bTop, ...aTop);
      });
    }
  }

  return new Float32Array(positions);
};
